import { Helmet } from "react-helmet-async";

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  structuredData?: object;
}

export function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage = "/og-image.png",
  structuredData,
}: SEOProps) {
  const fullTitle = `${title} | EVGateway - EV Charging Solutions`;
  const baseUrl = "https://evgateway.com";
  const canonical = canonicalUrl ? `${baseUrl}${canonicalUrl}` : baseUrl;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {/* Only add canonical if not homepage (index.html already has base canonical) */}
      {canonicalUrl && canonicalUrl !== "/" && <link rel="canonical" href={canonical} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={`${baseUrl}${ogImage}`} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${baseUrl}${ogImage}`} />

      {/* Structured Data */}
      {structuredData && <script type="application/ld+json">{JSON.stringify(structuredData)}</script>}
    </Helmet>
  );
}

// Pre-defined structured data schemas
export const homePageSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "EVGateway",
  url: "https://evgateway.com",
  potentialAction: {
    "@type": "SearchAction",
    target: "https://evgateway.com/search?q={search_term_string}",
    "query-input": "required name=search_term_string",
  },
};

export const solutionsPageSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "EV Charging Solutions",
  description:
    "Complete EV charging solutions including Public Charging, Fleet Charging, V2G Technology, and Smart Charging",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Public EV Charging",
      description: "High-visibility public EV charging infrastructure with DC fast charging up to 1 Megawatt",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Fleet Charging",
      description: "Smart depot charging and fleet management with 40% cost reduction",
    },
    {
      "@type": "ListItem",
      position: 3,
      name: "V2G Technology",
      description: "Vehicle-to-Grid bidirectional charging for revenue generation",
    },
    {
      "@type": "ListItem",
      position: 4,
      name: "Smart Charging",
      description: "Grid integration and smart load management with OpenADR certification",
    },
  ],
};

export const cpoPageSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Charge Point Operator Services",
  provider: {
    "@type": "Organization",
    name: "EVGateway",
  },
  description: "Complete CPO services for deploying and managing EV charging networks with NEVI compliance",
  areaServed: "North America",
  serviceType: "EV Charging Network Management",
};

export const productsPageSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "EVGateway EV Charging Platform",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web, iOS, Android",
  description: "Enterprise EV charging management platform with SaaS dashboard, mobile apps, and API integration",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    description: "Free demo available",
  },
};

export const driversPageSchema = {
  "@context": "https://schema.org",
  "@type": "MobileApplication",
  name: "EVGateway Driver App",
  operatingSystem: "iOS, Android",
  applicationCategory: "UtilitiesApplication",
  description: "Find and use EV charging stations with real-time availability, flexible payments, and 24/7 support",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
  },
};

export const faqSchema = (faqs: { question: string; answer: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});
